import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AgentLayout from '../../components/layout/AgentLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import {
  Search,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  Inbox,
  Loader2,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  Briefcase,
  Shield,
  Globe,
  BookOpen,
  Home,
  Plane,
  Target,
  ArrowUpRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { agentAPI } from '../../services/api';
import { useRoleBasePath } from '../../hooks/useRoleBasePath';

const SERVICE_TYPE_CONFIG = {
  PROFILE_ASSESSMENT: { label: 'Profile Assessment', icon: Target, bgColor: 'bg-blue-50', textColor: 'text-blue-700' },
  UNIVERSITY_SHORTLISTING: { label: 'Preapplication Support', icon: GraduationCap, bgColor: 'bg-purple-50', textColor: 'text-purple-700' },
  APPLICATION_ASSISTANCE: { label: 'Apply University', icon: FileText, bgColor: 'bg-pink-50', textColor: 'text-pink-700' },
  VISA_GUIDANCE: { label: 'Visa & Interview Support', icon: Shield, bgColor: 'bg-indigo-50', textColor: 'text-indigo-700' },
  SCHOLARSHIP_SEARCH: { label: 'Ticket & Travel Support', icon: BookOpen, bgColor: 'bg-amber-50', textColor: 'text-amber-700' },
  LOAN_ASSISTANCE: { label: 'Education Loan', icon: Briefcase, bgColor: 'bg-cyan-50', textColor: 'text-cyan-700' },
  ACCOMMODATION_HELP: { label: 'Find Accommodation', icon: Home, bgColor: 'bg-orange-50', textColor: 'text-orange-700' },
  PRE_DEPARTURE_ORIENTATION: { label: 'Find Jobs Abroad', icon: Plane, bgColor: 'bg-emerald-50', textColor: 'text-emerald-700' }
};

const STATUS_CONFIG = {
  PENDING_ADMIN_ASSIGNMENT: { label: 'Pending Assignment', color: 'bg-gray-100 text-gray-700' },
  ASSIGNED: { label: 'Assigned', color: 'bg-blue-100 text-blue-700' },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  WAITING_STUDENT: { label: 'Waiting on Student', color: 'bg-purple-100 text-purple-700' },
  ON_HOLD: { label: 'On Hold', color: 'bg-slate-100 text-slate-700' },
  COMPLETED: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-100 text-red-700' }
};

const SERVICE_TYPES = Object.keys(SERVICE_TYPE_CONFIG);

export default function AgentApplications() {
  const navigate = useNavigate();
  const basePath = useRoleBasePath();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');

  const fetchApplications = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (serviceTypeFilter && serviceTypeFilter !== 'all') params.serviceType = serviceTypeFilter;
      if (searchTerm) params.search = searchTerm;

      const result = await agentAPI.getServiceRequests(params);
      setApplications(result.serviceRequests || result.data || []);
      setPagination(result.pagination || { total: 0, page: 1, limit: 20, totalPages: 0 });
    } catch (err) {
      console.error('Failed to fetch applications:', err);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, serviceTypeFilter, searchTerm]);

  useEffect(() => {
    fetchApplications(1);
  }, [fetchApplications]);

  // Stats computed from current page data + total from pagination
  const totalApps = pagination.total;
  const pendingCount = applications.filter(a =>
    ['PENDING_ADMIN_ASSIGNMENT', 'ASSIGNED'].includes(a.status)
  ).length;
  const activeCount = applications.filter(a =>
    ['IN_PROGRESS', 'WAITING_STUDENT'].includes(a.status)
  ).length;
  const completedCount = applications.filter(a => a.status === 'COMPLETED').length;

  const getInitials = (app) => {
    const s = app.student;
    if (!s || !s.firstName) return '??';
    return `${s.firstName?.[0] || ''}${s.lastName?.[0] || ''}`;
  };

  const getStudentName = (app) => {
    const s = app.student;
    if (!s || !s.firstName) return 'Unknown Student';
    return `${s.firstName} ${s.lastName}`;
  };

  const getServiceLabel = (type) => {
    return SERVICE_TYPE_CONFIG[type]?.label || type?.replace(/_/g, ' ') || '-';
  };

  const getServiceIcon = (type) => {
    return SERVICE_TYPE_CONFIG[type]?.icon || FileText;
  };

  return (
    <AgentLayout>
      <div className="space-y-6" data-testid="agent-applications">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-outfit font-bold text-gray-900">Applications</h1>
          <p className="text-gray-500">Track your referred students' service applications</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4 bg-white border-0 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{totalApps}</p>
                <p className="text-sm text-gray-500">Total</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-0 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{pendingCount}</p>
                <p className="text-sm text-gray-500">Pending</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-0 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
                <p className="text-sm text-gray-500">Active</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-0 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{completedCount}</p>
                <p className="text-sm text-gray-500">Completed</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by student name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-gray-200"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-52">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={serviceTypeFilter} onValueChange={setServiceTypeFilter}>
              <SelectTrigger className="w-full md:w-52">
                <SelectValue placeholder="All Services" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {SERVICE_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{SERVICE_TYPE_CONFIG[type].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Table */}
        {loading ? (
          <Card className="p-12 bg-white border-0 shadow-sm flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
          </Card>
        ) : applications.length === 0 ? (
          <Card className="p-12 bg-white border-0 shadow-sm text-center">
            <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700">No Applications Found</h3>
            <p className="text-gray-500 mt-1">
              {searchTerm || statusFilter !== 'all' || serviceTypeFilter !== 'all'
                ? 'Try adjusting your filters.'
                : 'Service applications from your students will appear here.'}
            </p>
          </Card>
        ) : (
          <Card className="bg-white border-0 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="applications-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Student</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Service</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Progress</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Priority</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Applied</th>
                    <th className="text-right py-4 px-6 text-sm font-medium text-gray-500">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {applications.map((app, index) => {
                    const ServiceIcon = getServiceIcon(app.serviceType);
                    const svcConfig = SERVICE_TYPE_CONFIG[app.serviceType] || {};
                    const statusCfg = STATUS_CONFIG[app.status] || { label: app.status, color: 'bg-gray-100 text-gray-700' };
                    const progressVal = app.progress || 0;

                    return (
                      <motion.tr
                        key={app.serviceRequestId || index}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.03 }}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            {app.student?.avatar ? (
                              <img
                                src={app.student.avatar}
                                alt=""
                                className="w-9 h-9 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                                {getInitials(app)}
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{getStudentName(app)}</p>
                              <p className="text-xs text-gray-500">{app.student?.email || ''}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded-md flex items-center justify-center ${svcConfig.bgColor || 'bg-gray-50'}`}>
                              <ServiceIcon className={`w-3.5 h-3.5 ${svcConfig.textColor || 'text-gray-600'}`} />
                            </div>
                            <span className="text-sm text-gray-900">{getServiceLabel(app.serviceType)}</span>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  progressVal === 100 ? 'bg-green-500' : progressVal >= 50 ? 'bg-emerald-500' : 'bg-amber-500'
                                }`}
                                style={{ width: `${progressVal}%` }}
                              />
                            </div>
                            <span className="text-sm text-gray-600 w-8">{progressVal}%</span>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
                        </td>
                        <td className="py-4 px-6">
                          <Badge
                            variant="outline"
                            className={`text-xs capitalize ${
                              app.priority === 'URGENT' ? 'border-red-300 text-red-700' :
                              app.priority === 'HIGH' ? 'border-orange-300 text-orange-700' :
                              app.priority === 'LOW' ? 'border-gray-300 text-gray-500' :
                              'border-blue-300 text-blue-700'
                            }`}
                          >
                            {app.priority?.toLowerCase() || 'medium'}
                          </Badge>
                        </td>
                        <td className="py-4 px-6 text-gray-500 text-sm">
                          {app.appliedAt ? new Date(app.appliedAt).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-4 px-6 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`${basePath}/service-requests/${app.serviceRequestId}`)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page <= 1}
                    onClick={() => fetchApplications(pagination.page - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-600">Page {pagination.page} of {pagination.totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => fetchApplications(pagination.page + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Agent-Initiated Pending Approval Notice */}
        {applications.some(a => a.isAgentInitiated && a.agentApprovalStatus === 'PENDING_APPROVAL') && (
          <Card className="p-4 bg-amber-50 border border-amber-200 shadow-sm">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Pending Admin Approval</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  Some service applications you initiated are awaiting Super Admin approval before they can proceed.
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </AgentLayout>
  );
}
